<template>
  <div id="module-participation">
    <!-- Left side -->
    <div class="module-info" :style="{ backgroundColor: runningModule.themeColor }">
      <div class="module-img">
        <div
          :style="{
            height: '100%',
            backgroundImage: `url(${$path.resolveImage(
              runningModule.image,
              $assets.image.default['default-blank-module.png']
            )})`,
            backgroundSize: runningModule.image ? 'cover' : '100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }"
        />
      </div>

      <div class="module-details">
        <div class="module-details-top">
          <div class="module-theme">{{ runningModule.themeName }}</div>
          <div class="module-title">{{ runningModule.moduleName }}</div>
          <div class="module-estimated">
            {{ $filters.duration(runningModule.startTime, runningModule.endTime) }}
          </div>
        </div>

        <div class="module-facilitators">
          <div class="facilitator-list-title">
            <span>Facilitators:</span>
          </div>
          <ul>
            <li class="facilitators-list-item" v-for="facilitator in selectedFacilitatorList" :key="facilitator.id">
              {{ facilitator.name }}
            </li>
          </ul>
        </div>
      </div>

      <div class="facilitator-area">
        <button type="submit" class="facilitator-training" @click="completeTraining">Facilitator Training</button>
        <p>Select here if you are a facilitator reviewing the HUB module.</p>
      </div>
    </div>

    <!-- Right side -->
    <div class="participation">
      <div class="form-and-list">
        <!-- WOTR from community learning -->
        <template v-if="needsDemographics">
          <DemographicsForm @updateDemographicsInfo="updateDemographicsInfo" />
        </template>
        <!-- Regular community learning -->
        <template v-else-if="isCommunityLearning">
          <ReferralSelection @updateWorkshopInfo="updateWorkshopInfo" />
        </template>
        <!-- Justice learning -->
        <template v-else-if="isJusticeLearning">
          <ClientSelection
            :selectedIds="selectedClientsList.map((item) => item.id)"
            @updateClientList="updateClientList"
          />
        </template>
      </div>

      <section class="operations">
        <button :disabled="!enabledToSubmit" class="submit" type="submit" @click="complete">Submit</button>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, computed } from 'vue';
import { useRouter } from 'vue-router';

import $assets from '@/assets';
import ClientSelection from '@/components/participation/ClientSelection.vue';
import DemographicsForm from '@/components/participation/DemographicsForm.vue';
import ReferralSelection from '@/components/participation/ReferralSelection.vue';
import MetaService from '@/services/meta';
import RecordService from '@/services/record';
import useSessionStore from '@/stores/session';
import $path from '@/utils/path';

const $router = useRouter();
const SessionStore = useSessionStore();

const ageList = ref({});

const selectedFacilitatorList = ref([]);
//
const selectedClientsList = ref([]);
//
const participationCount = ref(0);
const minAge = ref(0);
const maxAge = ref(0);
//
const languageId = ref(0);
const provinceId = ref(0);
const demographicsList = ref([]);

const runningModule = computed(() => {
  return SessionStore.HUBSettings.runningModule;
});

const isJusticeLearning = computed(() => {
  return ['youth-justice', 'adult-justice'].includes(runningModule.value.sectorKey);
});

const isCommunityLearning = computed(() => {
  return ['youth-community', 'adult-community'].includes(runningModule.value.sectorKey);
});

const needsDemographics = computed(() => {
  return isCommunityLearning.value && runningModule.value.module_key === 'wotr';
});

const demographicsValid = computed(() => {
  const activatedDemos = demographicsList.value.slice(0, -1);

  return (
    languageId.value !== 0 &&
    provinceId.value !== 0 &&
    activatedDemos.every(
      (demoItem) =>
        !demoItem.error && demoItem.ageId !== 0 && demoItem.genderId !== 0 && /^[0-9]+$/.test(demoItem.numParticipants)
    )
  );
});

const enabledToSubmit = computed(() => {
  // WOTR from community learning
  if (needsDemographics.value) {
    return demographicsValid.value;
  }

  // Regular community learning
  if (isCommunityLearning.value) {
    return (
      Number(participationCount.value) > 0 &&
      Number(minAge.value) > 0 &&
      Number(maxAge.value) > 0 &&
      Number(minAge.value) <= Number(maxAge.value)
    );
  }

  // Justice learning
  if (isJusticeLearning.value) {
    return selectedFacilitatorList.value.length > 0 && selectedClientsList.value.length > 0;
  }

  return false;
});

onBeforeMount(async () => {
  const ageResutls = await MetaService.getMetasByType('age');
  ageResutls.records.forEach((age) => {
    ageList.value[age.id] = age.meta_value;
  });

  selectedFacilitatorList.value.push({
    id: runningModule.value.hostId,
    name: runningModule.value.hostName,
    selected: true,
  });
});

function updateClientList(list) {
  selectedClientsList.value = list;
}

function updateWorkshopInfo(info) {
  participationCount.value = info.participationCount;
  minAge.value = info.minAge;
  maxAge.value = info.maxAge;
}

function updateDemographicsInfo(data) {
  languageId.value = data.languageId;
  provinceId.value = data.provinceId;
  demographicsList.value = data.demoList;

  const parsedDemoList = demographicsList.value.filter(
    (demoItem) => demoItem.ageId !== 0 && demoItem.genderId !== 0 && /^[0-9]+$/.test(demoItem.numParticipants)
  );

  const ages = parsedDemoList.map((item) => parseInt(ageList.value[item.ageId], 10));

  if (ages.length > 0) {
    minAge.value = Math.min(...ages);
    maxAge.value = Math.max(...ages);
  }

  participationCount.value = parsedDemoList.reduce((acc, item) => acc + item.numParticipants, 0);
}

async function completeTraining() {
  const facilitatorIds = selectedFacilitatorList.value.map((facilitator) => facilitator.id);

  await RecordService.addRecord({
    runningId: runningModule.value.id,
    facilitatorIds,
    facilitatorTraining: true,
  });

  $router.push({ name: 'dashboard' });
}

async function completeDemographicsData() {
  // remove incomplete records from final demographics, in case the demographics
  // logic hadn't cut them out
  const parsedDemoList = demographicsList.value.filter(
    (demoItem) => demoItem.ageId !== 0 && demoItem.genderId !== 0 && /^[0-9]+$/.test(demoItem.numParticipants)
  );

  await RecordService.addRecord({
    themeId: runningModule.value.themeId,
    moduleId: runningModule.value.moduleId,
    runningId: runningModule.value.id,
    facilitatorIds: selectedFacilitatorList.value.map((facilitator) => facilitator.id),
    languageId: languageId.value,
    provinceId: provinceId.value,
    demographicsList: parsedDemoList,
    // Need to be calculate based on demo list
    minAge: minAge.value,
    maxAge: maxAge.value,
    participationCount: participationCount.value,
  });

  $router.push({ name: 'dashboard' });
}

async function complete() {
  if (needsDemographics.value) {
    completeDemographicsData();
    return;
  }

  const facilitatorIds = selectedFacilitatorList.value.map((facilitator) => facilitator.id);
  const clientIds = selectedClientsList.value.map((facilitator) => facilitator.id);

  await RecordService.addRecord({
    runningId: runningModule.value.id,
    facilitatorIds,
    clientIds,
    minAge: minAge.value,
    maxAge: maxAge.value,
    participationCount: participationCount.value,
  });

  $router.push({ name: 'dashboard' });
}
</script>

<style scoped>
#module-participation {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/** Left side */
.module-info {
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;
  color: #fff;
}
.module-info .module-img {
  height: 40%;
}

.module-details {
  flex-grow: 1;
  padding: 1em;
}
.module-details .module-details-top {
  display: grid;
  grid-template-areas:
    'a c'
    'b c';
}
.module-details .module-details-top .module-estimated {
  grid-area: c;
  text-align: right;
}
.module-details .module-details-top > div {
  line-height: 1.4em;
  font-size: 1.4em;
}

.module-facilitators {
  margin-top: 1em;
  line-height: 2em;
  font-size: 1.3em;
  font-weight: 100;
}
.module-facilitators .facilitator-list-title,
.module-facilitators .facilitators-list-item {
  border-bottom: 1px solid #fff;
}

.facilitator-area {
  padding: 2em;
  text-align: center;
}

.facilitator-area > .facilitator-training {
  width: 100%;
  border: none;
  border-radius: 0.5em;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.5);
  padding: 0.5em 0;
  background: #fefffd;
  color: #686464;
  font-family: Helvetica;
  font-size: 1.2em;
  cursor: pointer;
}
.facilitator-area > p {
  margin: 1em 0 0;
  padding: 0 1em;
}

/** Right side */
.participation {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.form-and-list {
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  padding: 0 1em;
  overflow: hidden;
}

.operations {
  padding: 3em 0 2em;
  text-align: center;
}

.submit {
  padding: 0.5em 4em;
  font-size: 1.3em;
  color: #fff;
  background: #8ac640;
  border: 1px solid #a7a9ac;
  border-radius: 0.3em;
  cursor: pointer;
}
.submit:disabled {
  cursor: not-allowed;
  filter: gray;
  filter: grayscale(100%);
}

.submit:hover {
  box-shadow: 0 0 0.3em 0 #a7a9ac;
}
.submit:disabled:hover {
  box-shadow: none;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  .module-info {
    width: 45%;
  }
  .module-details {
    padding: 1em;
  }
  .module-details .module-details-top > div {
    line-height: 1.1em;
    font-size: 1.1em;
  }
  .module-facilitators {
    margin-top: 1em;
    line-height: 1.5em;
    font-size: 1em;
  }
  .facilitator-area {
    padding: 0.5em 1em;
  }
  .facilitator-area > .facilitator-training {
    padding: 0.5em 0;
    font-size: 1em;
  }
  .facilitator-area > p {
    margin: 0.5em 0;
    padding: 0;
  }

  .participation {
    width: 55%;
    height: 100%;
    overflow-y: auto;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .module-info {
    display: flex;
    flex-direction: column;
    width: 40%;
    max-width: 45em;
  }

  .module-details {
    font-size: 1.1em;
  }

  .module-facilitators {
    font-size: 1.1em;
  }

  .participation {
    flex: 1;
    height: 100%;
    overflow-y: auto;
  }
}
</style>
